import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import api from '../../utils/api';

interface UserProfile {
  id: number;
  username: string;
  email: string;
  avatar?: string;
  bio?: string;
  role: string;
  followersCount: number;
  followingCount: number;
  booksCount: number;
}

interface Bookshelf {
  id: number;
  name: string;
  bookCount: number;
  description?: string;
  books?: {
    book: {
      id: number;
      title: string;
      cover?: string;
    };
  }[];
}

const Profile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookshelves, setBookshelves] = useState<Bookshelf[]>([]);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  const isOwner = isAuthenticated && user?.id === parseInt(id || '0', 10);

  useEffect(() => {
    fetchProfile();
    fetchBookshelves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${id}`);
      setProfile(data);
    } catch (error) {
      console.error('获取用户信息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookshelves = async () => {
    try {
      const { data } = await api.get(`/bookshelves/user/${id}`);
      // 获取每个书单的简要信息（最近5本）
      const shelvesWithBooks = await Promise.all(
        (data || []).map(async (shelf: Bookshelf) => {
          try {
            const { data: shelfData } = await api.get(`/bookshelves/${shelf.id}/recent`);
            return shelfData;
          } catch {
            return shelf;
          }
        })
      );
      setBookshelves(shelvesWithBooks);
    } catch (error) {
      console.error('获取书单失败:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <p>用户不存在</p>
      </div>
    );
  }

  return (
    <div>
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          <div 
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '36px',
              color: '#999'
            }}
          >
            {profile.username[0].toUpperCase()}
          </div>
          
          <div style={{ flex: 1 }}>
            <h2 style={{ marginBottom: '8px' }}>{profile.username}</h2>
            <p style={{ color: '#666', marginBottom: '16px' }}>
              {profile.role === 'admin' ? '管理员' : profile.role === 'author' ? '作者' : '读者'}
              {profile.bio && ` · ${profile.bio}`}
            </p>
            
            <div style={{ display: 'flex', gap: '24px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{profile.booksCount}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>图书</div>
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{profile.followersCount}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>粉丝</div>
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold' }}>{profile.followingCount}</div>
                <div style={{ fontSize: '12px', color: '#999' }}>关注</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 书单展示 */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>{isOwner ? '我的书单' : 'Ta 的书单'}</h3>
        {bookshelves.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {bookshelves.map(shelf => (
              <div 
                key={shelf.id}
                style={{ 
                  border: '1px solid #e8e8e8', 
                  borderRadius: '8px', 
                  padding: '16px',
                  background: '#fafafa'
                }}
              >
                <Link 
                  to={`/bookshelf/${shelf.id}`}
                  style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold',
                    color: '#333',
                    textDecoration: 'none',
                    display: 'block',
                    marginBottom: '8px'
                  }}
                >
                  {shelf.name}
                </Link>
                <p style={{ fontSize: '12px', color: '#999', marginBottom: '12px' }}>
                  {shelf.bookCount} 本图书
                </p>
                
                {/* 最近添加的5本书 */}
                {shelf.books && shelf.books.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {shelf.books.map((item, idx) => (
                      <Link 
                        key={idx}
                        to={`/book/${item.book.id}`}
                        style={{ textDecoration: 'none' }}
                        title={item.book.title}
                      >
                        {item.book.cover ? (
                          <img 
                            src={item.book.cover} 
                            alt={item.book.title}
                            style={{ 
                              width: '40px', 
                              height: '56px', 
                              objectFit: 'cover',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          <div 
                            style={{ 
                              width: '40px', 
                              height: '56px', 
                              background: '#ddd',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              color: '#999'
                            }}
                          >
                            无
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
            暂无书单
          </p>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '16px' }}>Ta 的图书</h3>
        <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
          暂无图书
        </p>
      </div>
    </div>
  );
};

export default Profile;
